import { createContext } from 'preact';
import { ReactNode } from 'preact/compat';
import { useContext, useEffect, useMemo } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import { AppState, useSelector } from '@/redux/helper';
import { selectHasExplorerFeature } from '@/redux/selectors/users';
import appEvents from '@/shared/appEvents';
import { createSessionTimer, SessionTimer } from '@/shared/session';

import { flagShouldShowAd, setShouldShowAdOnNewSession } from './redux/actions';

export const SessionTimerContext = createContext<SessionTimer | undefined>(undefined);

export const useSessionTimer = () => useContext(SessionTimerContext);

const LOCAL_STORAGE_KEY = 'zl_session_timer';

const SessionTimerController = () => {
  const sessionTimer = useSessionTimer();
  const shouldShowAd = useSelector((state) => state.settings.advertisements.shouldShowAd);

  useEffect(() => {
    if (!sessionTimer) {
      return;
    }

    if (shouldShowAd) {
      sessionTimer.pause();
    } else {
      sessionTimer.resetCountdown();
      sessionTimer.resume();
    }
  }, [sessionTimer, shouldShowAd]);

  return null;
};

export const SessionTimerProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useDispatch();
  const logged = useSelector((state: AppState) => state.user.logged);
  const isFreeUser = !useSelector(selectHasExplorerFeature);
  const sessionTimeout = useSelector((state) => state.remoteConfig.advertisementConfig?.sessionTimeout);
  const nextAdCountdown = useSelector((state) => state.remoteConfig.advertisementConfig?.nextAdCountdown);

  const sessionTimer = useMemo(() => {
    const isEligible = !logged || isFreeUser;
    if (!isEligible || sessionTimeout == null || nextAdCountdown == null) {
      return undefined;
    }

    return createSessionTimer({
      sessionTimeoutMs: sessionTimeout,
      notificationIntervalMs: nextAdCountdown,
      storageKey: LOCAL_STORAGE_KEY,
    });
  }, [logged, isFreeUser, sessionTimeout, nextAdCountdown]);

  useEffect(() => {
    if (!sessionTimer) {
      return undefined;
    }
    sessionTimer
      .on('notification', () => {
        dispatch(flagShouldShowAd(true));
      })
      .on('newSession', () => {
        dispatch(setShouldShowAdOnNewSession());
      });
    sessionTimer.init();
    return () => {
      sessionTimer.destroy();
    };
  }, [sessionTimer, dispatch]);

  useEffect(() => {
    const handleLogout = () => {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    };
    appEvents.on('user:logout', handleLogout);
    return () => {
      appEvents.off('user:logout', handleLogout);
    };
  }, []);

  return (
    <SessionTimerContext.Provider value={sessionTimer}>
      {children}
      <SessionTimerController />
    </SessionTimerContext.Provider>
  );
};
